import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ExternalLink, Mail, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import {
  LEGAL_EFFECTIVE_DATE,
  LEGAL_LINKS,
  LEGAL_RESPONSIBLE,
} from "@/features/legal/legal-config";

export function LegalDocument({
  eyebrow,
  title,
  summary,
  children,
  showDocumentNav = true,
}: {
  eyebrow: string;
  title: string;
  summary: string;
  children: ReactNode;
  showDocumentNav?: boolean;
}) {
  return (
    <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="min-w-0 flex-1">
        <header className="min-w-0 border-b border-white/15 bg-black text-white">
          <div className="mx-auto min-w-0 max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
            <Link
              to="/legal"
              className="inline-flex items-center gap-2 text-xs font-semibold text-white/65 transition hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Centro legal
            </Link>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-white/65">
              {eyebrow}
            </p>
            <h1 className="mt-2 max-w-4xl break-words text-4xl font-semibold leading-tight tracking-[-0.055em] sm:text-6xl">
              {title}
            </h1>
            <p className="mt-5 max-w-3xl break-words text-sm leading-7 text-white/70 sm:text-base">
              {summary}
            </p>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/15 pt-5 text-xs text-white/65">
              <span>Vigente desde el {LEGAL_EFFECTIVE_DATE}</span>
              <span>Responsable: {LEGAL_RESPONSIBLE.name}</span>
            </div>
          </div>
        </header>

        <div className="mx-auto grid min-w-0 max-w-5xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[minmax(0,1fr)_240px]">
          <article className="min-w-0 space-y-11">{children}</article>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:h-fit">
            <div className="rounded-xl border border-black/15 bg-[#f7f6f1] p-5">
              <ShieldCheck className="h-5 w-5" />
              <p className="mt-4 text-sm font-semibold">¿Necesitas ayuda?</p>
              <p className="mt-2 text-xs leading-5 text-black/60">
                Escríbenos y menciona el documento o la compra relacionada. Nuestro objetivo es
                responder en {LEGAL_RESPONSIBLE.responseTime}.
              </p>
              <a
                href={`mailto:${LEGAL_RESPONSIBLE.email}`}
                className="mt-4 inline-flex items-center gap-2 break-all text-xs font-semibold underline underline-offset-4"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {LEGAL_RESPONSIBLE.email}
              </a>
            </div>

            {showDocumentNav && (
              <nav
                className="rounded-xl border border-black/15 p-5"
                aria-label="Documentos legales"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/55">
                  Documentos relacionados
                </p>
                <div className="mt-3 divide-y divide-black/10">
                  {LEGAL_LINKS.slice(1, 6).map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center justify-between gap-3 py-3 text-xs font-medium text-black/65 transition hover:text-black"
                    >
                      {item.label}
                      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  ))}
                </div>
              </nav>
            )}
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export function LegalSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-sm leading-7 text-black/70 [&_a]:font-medium [&_a]:text-black [&_a]:underline [&_a]:underline-offset-4 [&_code]:rounded [&_code]:bg-black/[0.06] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.92em] [&_code]:text-black [&_li]:pl-1 [&_strong]:font-semibold [&_strong]:text-black [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}

export function LegalCallout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-black bg-[#f7f6f1] p-5 sm:p-6">
      <p className="text-sm font-semibold text-black">{title}</p>
      <div className="mt-2 text-sm leading-6 text-black/65">{children}</div>
    </div>
  );
}

export function ExternalAuthorityLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5">
      {children}
      <ExternalLink className="inline h-3.5 w-3.5" aria-hidden="true" />
    </a>
  );
}
